import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { AccountService } from 'lib/services/account/account.service';
import { AuthService } from 'lib/services/auth.service';
import { CartService } from 'lib/services/cart/cart.service';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import get from 'lodash-es/get';
import { GrecaptchaService } from 'lib/services/google/grecaptcha.service';
import { MessageService } from 'lib/services/message.service';
import { SessionService } from 'lib/services/session.service';

const scriptName = 'auth-create-password.component';

/**
 * Guest account claim screen. A guest who logs in with the temporary password emailed at
 * checkout lands here (routed from auth-login with the temp password in router state). Setting
 * a real password converts the guest into a full customer on the server (registrationType flips
 * to 'customer'), preserving their order history.
 */
@Component({
    selector: 'app-auth-create-password',
    templateUrl: './auth-create-password.component.html',
    styleUrls: ['./auth-create-password.component.scss'],
    standalone: false,
})
export class AuthCreatePasswordComponent implements OnInit {
    actionPending: boolean = false;
    buttonClicked: boolean = false;
    createPasswordForm: FormGroup;
    isBrowser: boolean;
    showLabels: boolean = true;
    private tempPassword: string;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private accountService: AccountService,
        private authService: AuthService,
        private cartService: CartService,
        private formBuilder: FormBuilder,
        private grecaptchaService: GrecaptchaService,
        private router: Router,
        private sessionService: SessionService,
        public messageService: MessageService
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    ngOnInit() {
        try {
            // The temp password is passed via in-memory router state from the login screen. If
            // it's missing (e.g. the page was refreshed or opened directly), send the shopper
            // back to log in with the temporary password from their email.
            const nav = this.router.getCurrentNavigation();
            this.tempPassword = get(nav, 'extras.state.tempPassword') || (this.isBrowser ? get(history, 'state.tempPassword') : null);
            if (this.isBrowser && !this.tempPassword) {
                this.router.navigateByUrl('/account/login');
                return;
            }
            this.defineForm();
        } catch (err) {
            console.error(...new ExxComError(729401, scriptName, err).stamp());
        }
    }

    private defineForm() {
        try {
            this.createPasswordForm = this.formBuilder.group(
                {
                    password: ['', [Validators.required]],
                    retypePassword: ['', [Validators.required]],
                },
                { validators: this.validatePasswordsMatch, updateOn: 'change' }
            );
        } catch (err) {
            console.error(...new ExxComError(729402, scriptName, err).stamp());
        }
    }

    validatePasswordsMatch(formGroup: FormGroup) {
        try {
            const password = formGroup.controls.password;
            const retypePassword = formGroup.controls.retypePassword;
            if (retypePassword.errors && !retypePassword.errors.match) {
                return;
            }
            if (password.value != retypePassword.value) {
                retypePassword.setErrors({ match: true });
            } else {
                retypePassword.setErrors(null);
            }
        } catch (err) {
            console.error(...new ExxComError(729403, scriptName, err).stamp());
        }
    }

    get password() {
        return this.createPasswordForm.get('password');
    }
    get retypePassword() {
        return this.createPasswordForm.get('retypePassword');
    }

    hasError(control: any) {
        return control.invalid && (control.dirty || control.touched);
    }
    hasErrorRequired(control: any) {
        return this.hasError(control) && control.errors && control.errors.required;
    }
    hasErrorMatch(control: any) {
        return this.hasError(control) && control.errors && control.errors.match;
    }

    async onSubmit() {
        try {
            if (!this.isBrowser) {
                return;
            }
            if (this.createPasswordForm.invalid) {
                return this.createPasswordForm.markAllAsTouched();
            }
            this.buttonClicked = true;
            this.actionPending = true;
            await this.createPassword();
            this.actionPending = false;
        } catch (err) {
            this.actionPending = false;
            console.error(...new ExxComError(729404, scriptName, err).stamp());
        }
    }

    private async createPassword() {
        try {
            const res = await this.authService.password({
                action: 'changePassword',
                currentPassword: this.tempPassword,
                password: this.createPasswordForm.value.password,
                grecaptchaToken: await this.grecaptchaService.getToken('auth_createPassword'),
            });
            if (res.isServer) {
                return;
            }
            if (!res.success) {
                if (get(res, 'error.message') == 'Incorrect current password') {
                    // Temp password no longer valid (already claimed or expired) — restart login.
                    this.messageService.addLocal('login', 'Please log in again with the temporary password from your email.').clearOnAction();
                    this.router.navigateByUrl('/account/login');
                    return;
                }
                this.messageService.addLocal('createPassword', 'Server error').clearOnAction();
                throw res;
            }
            // Account is now a full customer. Refresh the session and land on the account page.
            this.sessionService.setIsGuestSession(false);
            this.sessionService.set(res.data.idToken, res.data.expiresIn, res.data.email).start();
            await this.accountService.init(true);
            await this.cartService.onLogin();
            this.router.navigateByUrl('/account');
        } catch (err) {
            if (get(err, 'error.message') == 'crg') {
                return;
            } // Ignore invalid reCAPTCHA response
            console.error(...new ExxComError(729405, scriptName, err).stamp());
        }
    }
}
